import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Building, CheckCircle2, AlertTriangle, HelpCircle, FileText, 
  ArrowRight, ExternalLink, Info
} from 'lucide-react';
import api from '../api';
import logger from '../utils/logger';

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmé',
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle2,
    description: 'Valeur vérifiée dans les règlements officiels'
  },
  estimated: {
    label: 'Estimé',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: AlertTriangle,
    description: 'Basé sur les valeurs typiques pour ce type de zone'
  },
  assumption: {
    label: 'Hypothèse',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: HelpCircle,
    description: 'Valeur par défaut ou fournie par l\'utilisateur'
  }
};

const ZoneSelector = ({ projectId, currentZoneCode, onZoneApplied }) => {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [activeCategory, setActiveCategory] = useState('residential');

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    setLoading(true);
    try {
      const response = await api.get('/zoning-database');
      setZones(response.data.rules || []);
    } catch (error) {
      logger.error('Erreur lors du chargement des zones:', error);
    }
    setLoading(false);
  };

  const handleZoneSelect = (zoneCode) => {
    const zone = zones.find(z => z.zone_code === zoneCode);
    setSelectedZone(zone);
    setShowDetails(true);
  };

  const applyTemplate = async () => {
    if (!selectedZone) return;
    
    setApplying(true);
    try {
      await api.post(`/zoning/apply-template/${projectId}?zone_code=${selectedZone.zone_code}`);
      if (onZoneApplied) {
        onZoneApplied(selectedZone);
      }
      setShowDetails(false);
    } catch (error) {
      logger.error('Erreur lors de l\'application du template:', error);
    }
    setApplying(false);
  };

  const filteredZones = zones.filter(z => z.zone_category === activeCategory);

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.estimated;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Zone Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="residential">Résidentiel</TabsTrigger>
          <TabsTrigger value="mixed_use">Mixte</TabsTrigger>
          <TabsTrigger value="commercial">Commercial</TabsTrigger>
          <TabsTrigger value="industrial">Industriel</TabsTrigger>
        </TabsList>

        <TabsContent value={activeCategory} className="mt-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredZones.map((zone) => (
                <Card 
                  key={zone.zone_code}
                  className={`cursor-pointer transition-all hover:border-primary/50 ${
                    currentZoneCode === zone.zone_code ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => handleZoneSelect(zone.zone_code)}
                  data-testid={`zone-card-${zone.zone_code}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-primary" />
                          <span className="font-mono font-bold text-lg">{zone.zone_code}</span>
                          {currentZoneCode === zone.zone_code && (
                            <Badge variant="secondary" className="text-xs">Actuel</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{zone.zone_name}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="bg-muted px-2 py-1 rounded">{zone.max_storeys} étages max</span>
                      <span className="bg-muted px-2 py-1 rounded">{(zone.lot_coverage * 100).toFixed(0)}% couverture</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Zone Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedZone && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-xl">{selectedZone.zone_code}</span>
                  <span className="text-lg font-normal">- {selectedZone.zone_name}</span>
                </DialogTitle>
                <DialogDescription>
                  {selectedZone.notes}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Permitted Uses */}
                <div>
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Usages permis
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedZone.permitted_uses?.map((use, i) => (
                      <Badge key={i} variant="outline" className="bg-green-50 text-green-700">
                        {use}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Conditional Uses */}
                {selectedZone.conditional_uses?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      Usages conditionnels
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedZone.conditional_uses.map((use) => (
                        <Badge key={use} variant="outline" className="bg-yellow-50 text-yellow-700">
                          {use}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Parameters */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Paramètres de construction</h4>
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Hauteur max</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{selectedZone.max_storeys} étages / {selectedZone.max_height_meters}m</span>
                        {renderStatusBadge(selectedZone.field_status?.max_storeys || 'estimated')}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Taux d&apos;implantation</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{(selectedZone.lot_coverage * 100).toFixed(0)}%</span>
                        {renderStatusBadge(selectedZone.field_status?.lot_coverage || 'estimated')}
                      </div>
                    </div>
                    
                    {selectedZone.floor_area_ratio && (
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">COS (FAR)</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{selectedZone.floor_area_ratio}</span>
                          {renderStatusBadge(selectedZone.field_status?.floor_area_ratio || 'estimated')}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Marges de recul</h4>
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Avant</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{selectedZone.front_setback}m</span>
                        {renderStatusBadge(selectedZone.field_status?.front_setback || 'estimated')}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Latérale</span>
                      <span className="font-medium">{selectedZone.side_setback}m</span>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Arrière</span>
                      <span className="font-medium">{selectedZone.rear_setback}m</span>
                    </div>
                  </div>
                </div>

                {/* Parking */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Stationnement</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{selectedZone.parking_requirements}</span>
                    {renderStatusBadge(selectedZone.field_status?.parking_requirements || 'estimated')}
                  </div>
                </div>

                {/* Special Provisions */}
                {selectedZone.special_provisions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <Info className="w-4 h-4 text-blue-600" />
                      Dispositions particulières
                    </h4>
                    <ul className="text-sm space-y-1">
                      {selectedZone.special_provisions.map((provision) => (
                        <li key={provision} className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          {provision}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Regulatory Reference */}
                <div className="bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Référence:</span>
                    <span className="font-medium">{selectedZone.regulatory_reference}</span>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDetails(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={applyTemplate} 
                  disabled={applying}
                  data-testid="apply-zone-template-btn"
                >
                  {applying ? 'Application...' : `Appliquer ${selectedZone.zone_code}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ZoneSelector;
